import Routes from 'routes'
import debug from 'debug'

const log = debug('app:javascript/clients/event_relay/jwt_token_manager.js')

// Token management constants
const TOKEN_REFRESH_BUFFER_MS = 30000 // refresh this long before expiry
const MIN_REFRESH_DELAY_MS = 30000 // never refetch sooner than this, whatever the arithmetic says
const DEFAULT_TOKEN_TTL_MS = 600000 // matches TokenService#default_expiration (10 minutes)
const TOKEN_ENDPOINT = Routes.tokens_eventing_token_index_url()

// Token state
let currentToken = null
let tokenExpiresAt = null
let tokenRefreshTimer = null
let tokenFetchPromise = null

// The response's `expiration` is an absolute epoch second on the *server's* clock, so comparing it
// against Date.now() is only valid when the two clocks agree. Schedule from the relative
// `expires_in` instead, which carries no clock.
function expiresAtFromResponse(data) {
  // Field absent: server predates ILC-2405. The only case where assuming a TTL is safe.
  if (data.expires_in === undefined || data.expires_in === null) {
    log('No expires_in in response, assuming default TTL')
    return Date.now() + DEFAULT_TOKEN_TTL_MS
  }

  const ttlMs = Number(data.expires_in) * 1000
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    // A non-positive TTL means the token is dead on arrival. Falling back to an assumed TTL here
    // would leave us holding a dead credential while every publish is rejected by the relay.
    throw new Error(`Invalid expires_in: ${data.expires_in}`)
  }

  return Date.now() + ttlMs
}

async function fetchTokenUncached() {
  try {
    log('Fetching new eventing token')
    const response = await fetch(TOKEN_ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Token fetch failed: ${response.status}`)
    }

    const data = await response.json()
    const token = data.jwt

    if (!token) {
      throw new Error('No jwt in response')
    }

    currentToken = token
    tokenExpiresAt = expiresAtFromResponse(data)

    log('Token fetched successfully, expires at:', new Date(tokenExpiresAt))

    // Schedule refresh
    scheduleTokenRefresh()

    return token
  } catch (error) {
    log('Error fetching token:', error)
    currentToken = null
    tokenExpiresAt = null
    throw error
  }
}

// Callers share one in-flight request, so a publish arriving during a scheduled refresh cannot stack
// a second fetch on top of it.
async function fetchToken() {
  if (tokenFetchPromise) return tokenFetchPromise

  tokenFetchPromise = fetchTokenUncached()
  try {
    return await tokenFetchPromise
  } finally {
    tokenFetchPromise = null
  }
}

function scheduleTokenRefresh() {
  // Clear any existing refresh timer
  if (tokenRefreshTimer) {
    clearTimeout(tokenRefreshTimer)
    tokenRefreshTimer = null
  }

  // Also covers NaN, which would otherwise reach setTimeout below and be coerced to a 0ms delay.
  if (!tokenExpiresAt) return

  // Floored deliberately.
  const delay = Math.max(tokenExpiresAt - Date.now() - TOKEN_REFRESH_BUFFER_MS, MIN_REFRESH_DELAY_MS)

  log(`Scheduling token refresh in ${Math.round(delay / 1000)} seconds`)
  tokenRefreshTimer = setTimeout(() => {
    // fetchTokenUncached already logs the failure; caught here only to avoid an unhandled rejection.
    fetchToken().catch(() => {})
  }, delay)
}

async function ensureValidToken() {
  if (!currentToken || !tokenExpiresAt) {
    await fetchToken()
    return currentToken
  }

  if (tokenExpiresAt - Date.now() <= TOKEN_REFRESH_BUFFER_MS) {
    // Token is about to expire, refresh it
    await fetchToken()
  }

  return currentToken
}

// Initialize token on module load
function initializeToken() {
  fetchToken().catch((error) => {
    log('Failed to initialize token:', error)
  })
}

export { ensureValidToken, initializeToken }
